import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Header } from '@/pages/public/landing/components/Header';
import { Footer } from '@/pages/public/landing/components/Footer';
import { useGetRepasseTable } from '@/services/queries/service';
import {
	WHATSAPP_LINK_FORM,
	WHATSAPP_NUMBER_FORMATTED,
} from '@/constants/whatsapp';

const flowSteps = [
	{
		title: 'Paciente paga pela plataforma.',
		small: 'O cartão (ou outro método aceito pela Stripe) é debitado no momento do agendamento ou conforme o plano contratado.',
	},
	{
		title: 'Stripe processa e liquida o valor para a Plumaa.',
		small: 'A liquidação obedece ao prazo padrão da Stripe Brasil — geralmente em poucos dias úteis após a sessão.',
	},
	{
		title: 'Assim que recebemos, repassamos para você via Pix.',
		small: 'O Pix vai para a chave bancária PJ que você cadastrou no perfil. O envio é feito assim que o valor cai na conta da Plumaa — sem ciclos fechados de espera.',
	},
];

const valueRules = [
	<>
		<strong>Paciente faltou e você ficou ≥ 15 minutos na sala.</strong> A
		sessão é considerada realizada parcialmente — você recebe{' '}
		<strong>metade do valor</strong>, conforme sua especialidade na tabela
		acima.
	</>,
	<>
		<strong>Paciente cancelou com menos de 24h.</strong> Você recebe{' '}
		<strong>metade do valor</strong> da sessão, conforme sua especialidade
		na tabela acima — ainda que tenha consentido com o cancelamento.
	</>,
	<>
		<strong>Você faltou (no-show profissional injustificado).</strong> Sem
		repasse para a sessão. Reincidência sujeita às sanções do contrato.
	</>,
	<>
		<strong>
			Cancelamento por sua iniciativa, com aviso prévio adequado.
		</strong>{' '}
		Sem cobrança e sem repasse — o paciente é reagendado ou reembolsado.
	</>,
	<>
		<strong>
			Reembolso ao paciente por falha grave atribuível a você.
		</strong>{' '}
		O valor pode ser compensado em repasses futuros, conforme o contrato de
		credenciamento.
	</>,
];

const tableChangeRules = [
	<>
		Os <strong>novos valores não retroagem</strong> a sessões já confirmadas
		e agendadas antes da publicação.
	</>,
	<>
		Os{' '}
		<strong>
			novos valores valem para todas as sessões agendadas após
		</strong>{' '}
		a publicação da nova tabela.
	</>,
	<>
		Você recebe <strong>aviso por e-mail</strong> com a data de vigência
		antes da virada.
	</>,
	<>
		Esta página exibe sempre a versão vigente, com a data de{' '}
		<strong>&quot;Última atualização&quot;</strong> no topo.
	</>,
];

const brlFormatter = new Intl.NumberFormat('pt-BR', {
	style: 'currency',
	currency: 'BRL',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
	day: '2-digit',
	month: 'long',
	year: 'numeric',
});

/** "50" | "120.5" -> "R$ 50,00" | "R$ 120,50". */
const formatRepasse = (value: string | null): string => {
	const num = Number(value);
	if (value == null || Number.isNaN(num)) return '—';
	return brlFormatter.format(num);
};

const ProfessionalRepassePage = () => {
	const { data: repasseData, isLoading: isLoadingRepasse } =
		useGetRepasseTable();

	// Só exibe especialidades com repasse definido pelo admin.
	const repasseRows = (repasseData ?? []).filter(
		(row) => row.professional_share_value != null,
	);

	// "Última atualização" = edição mais recente entre os valores publicados.
	const lastUpdatedLabel = (() => {
		const timestamps = repasseRows
			.map((row) => new Date(row.updated_at).getTime())
			.filter((t) => !Number.isNaN(t));
		if (timestamps.length === 0) return null;
		return dateFormatter.format(new Date(Math.max(...timestamps)));
	})();

	return (
		<section className="flex w-full flex-col items-center bg-white text-zinc-900">
			<Helmet>
				<title>Plumaa · Tabela de remuneração — Profissionais</title>
				<meta
					name="description"
					content="Tabela oficial de remuneração para profissionais credenciados na Plumaa (psicólogos, nutricionistas e médicos especialistas em saúde mental). Valor único por sessão e especialidade, fluxo de pagamento."
				/>
				<meta name="robots" content="noindex, follow" />
			</Helmet>

			<Header />

			<main className="w-full border-y border-zinc-200 bg-white px-6 py-12 md:px-10 md:py-14">
				<section className="mx-auto max-w-[820px] border-b border-zinc-200 pb-8">
					<p className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-orange">
						Documento de referência
					</p>
					<h1 className="font-libre text-[34px] font-bold leading-[1.08] tracking-[-0.015em] text-zinc-900 md:text-[46px]">
						Tabela de remuneração
						<br />
						<em className="text-brand-orange">
							para profissionais credenciados.
						</em>
					</h1>
					<p className="font-albert mt-4 max-w-[640px] text-[15.5px] leading-[1.7] text-zinc-600">
						Esta página existe pra que você consulte, a qualquer
						momento, quanto a Plumaa paga por sessão e como o
						pagamento chega na sua conta. É a versão oficial e
						vigente.
					</p>
					<p className="font-albert mt-4 text-[12.5px] text-zinc-500">
						Última atualização:{' '}
						<strong className="font-semibold text-zinc-900">
							{lastUpdatedLabel ?? '—'}
						</strong>{' '}
						· vigente
					</p>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						De onde vem o paciente
					</h2>
					<div className="font-albert space-y-3.5 text-[14.5px] leading-[1.75] text-zinc-600">
						<p>
							Os pacientes que chegam na sua agenda vêm, na
							maioria, de empresas que contrataram a Plumaa para
							cumprir a NR-1 — a norma do Ministério do Trabalho
							que obriga toda empresa a cuidar dos riscos
							psicossociais dos colaboradores.
						</p>
						<p>
							Antes de chegar até você, o colaborador já passou
							por uma{' '}
							<strong className="font-semibold text-zinc-900">
								avaliação digital
							</strong>{' '}
							dentro da plataforma — ou seja, o perfil clínico
							está mapeado. Você não precisa captar paciente,
							fazer marketing nem manter consultório aberto.{' '}
							<strong className="font-semibold text-zinc-900">
								O agendamento chega naturalmente
							</strong>
							, conforme a sua disponibilidade publicada na
							plataforma e o perfil indicado para o caso.
						</p>
						<p>
							Há ainda os pacientes do canal direto ao consumidor
							(planos individuais Plumaa). O fluxo de remuneração
							é o mesmo desta tabela, independente da origem.
						</p>
					</div>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Valor por sessão
					</h2>
					<div className="font-albert space-y-3.5 text-[14.5px] leading-[1.75] text-zinc-600">
						<p>
							O repasse é um{' '}
							<strong className="font-semibold text-zinc-900">
								valor único por especialidade
							</strong>
							, independente do horário da sessão. Toda sessão
							começa em{' '}
							<strong className="font-semibold text-zinc-900">
								hora cheia
							</strong>{' '}
							(08:00, 09:00, 10:00 etc.) e dura 50 minutos, com 10
							minutos de pausa antes da próxima.
						</p>
						<p>
							Exemplo: quem abre a janela de atendimento das{' '}
							<strong className="font-semibold text-zinc-900">
								06:00 às 10:00
							</strong>{' '}
							fica disponível para receber até quatro consultas —
							das 06:00 às 06:50, das 07:00 às 07:50, das 08:00 às
							08:50 e das 09:00 às 09:50. Abertura de janela não
							significa que todos os slots serão preenchidos:
							depende da demanda da plataforma.
						</p>
					</div>

					<div className="mt-5 overflow-hidden rounded-xl border border-zinc-200 bg-white">
						<table className="w-full border-collapse">
							<thead>
								<tr className="border-b border-zinc-200">
									<th className="font-albert rounded-tl-xl border-b border-zinc-200 bg-zinc-100 px-3 py-4 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600">
										Profissional
									</th>
									<th className="font-albert w-[11rem] rounded-tr-xl border-b border-zinc-200 bg-zinc-100 px-3 py-4 text-center text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600">
										Repasse por sessão
									</th>
								</tr>
							</thead>
							<tbody>
								{isLoadingRepasse ? (
									<tr>
										<td
											colSpan={2}
											className="font-albert px-3 py-8 text-center text-[13px] text-zinc-400"
										>
											Carregando tabela de remuneração...
										</td>
									</tr>
								) : repasseRows.length === 0 ? (
									<tr>
										<td
											colSpan={2}
											className="font-albert px-3 py-8 text-center text-[13px] text-zinc-400"
										>
											Nenhum valor de repasse publicado no
											momento.
										</td>
									</tr>
								) : (
									repasseRows.map((row) => (
										<tr
											key={row.id}
											className="border-b border-zinc-100 last:border-b-0"
										>
											<td className="min-w-0 px-3 py-5 align-middle">
												<div className="font-libre text-[26px] leading-[1.1] text-zinc-900">
													{row.professional_label ||
														row.service_name}
												</div>
												<div className="font-albert mt-1 text-[12.5px] text-zinc-500">
													{row.service_name}
												</div>
											</td>
											<td className="w-[11rem] px-3 py-5 text-center align-middle">
												<div className="font-libre whitespace-nowrap text-[28px] leading-none text-brand-orange md:text-[30px]">
													{formatRepasse(
														row.professional_share_value,
													)}
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<p className="font-albert mt-3 text-[12.5px] leading-[1.65] text-zinc-500">
						Valor único por especialidade, válido para qualquer
						horário. Todos os valores acima referem-se a sessões de
						50 minutos efetivos.
					</p>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Duração e ritmo das sessões
					</h2>
					<p className="font-albert text-[14.5px] leading-[1.75] text-zinc-600">
						A estrutura padrão da agenda Plumaa segue o protocolo
						clínico mais usado no Brasil:
					</p>
					<div className="mt-4 grid grid-cols-1 gap-3.5 md:grid-cols-2">
						<div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
							<div className="font-albert mb-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500">
								Duração da sessão
							</div>
							<div className="font-libre text-[24px] text-zinc-900">
								50 minutos
							</div>
							<p className="font-albert mt-1.5 text-[12.5px] leading-[1.6] text-zinc-600">
								Tempo efetivo de atendimento clínico. É o que
								entra no contador para fins de remuneração.
							</p>
						</div>
						<div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
							<div className="font-albert mb-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500">
								Pausa entre sessões
							</div>
							<div className="font-libre text-[24px] text-zinc-900">
								10 minutos
							</div>
							<p className="font-albert mt-1.5 text-[12.5px] leading-[1.6] text-zinc-600">
								Intervalo padrão entre uma sessão e a próxima —
								para registro pós-sessão, pausa pessoal e
								preparação.
							</p>
						</div>
					</div>
					<p className="font-albert mt-4 text-[14.5px] leading-[1.75] text-zinc-600">
						Ou seja: cada paciente ocupa{' '}
						<strong className="font-semibold text-zinc-900">
							1 hora
						</strong>{' '}
						da sua agenda (50 min de atendimento + 10 min de pausa),
						ainda que o repasse seja calculado sobre os 50 minutos
						efetivos.
					</p>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Como o pagamento chega até você
					</h2>
					<p className="font-albert text-[14.5px] leading-[1.75] text-zinc-600">
						O processamento financeiro é feito pela{' '}
						<strong className="font-semibold text-zinc-900">
							Stripe
						</strong>
						, plataforma global de pagamentos integrada à Plumaa. O
						fluxo é o seguinte:
					</p>

					<ol className="mt-3 list-none">
						{flowSteps.map((step, index) => (
							<li
								key={step.title}
								className="grid grid-cols-[28px_1fr] gap-3.5 border-b border-zinc-100 py-3.5 last:border-b-0"
							>
								<span className="font-albert mt-0.5 grid h-7 w-7 place-items-center rounded-full bg-zinc-200 text-[12px] font-bold text-zinc-700">
									{index + 1}
								</span>
								<div className="font-albert text-[14px] leading-[1.7] text-zinc-900">
									<strong className="font-semibold">
										{step.title}
									</strong>
									<small className="mt-0.5 block text-[12.5px] leading-[1.6] text-zinc-500">
										{step.small}
									</small>
								</div>
							</li>
						))}
					</ol>

					<div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-5">
						<div className="font-albert mb-2 text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-600">
							Pré-requisito do repasse
						</div>
						<p className="font-albert text-[14px] leading-[1.7] text-zinc-800">
							O Pix é liberado mediante a{' '}
							<strong className="font-semibold">
								Nota Fiscal de Serviços Eletrônica (NFS-e)
							</strong>{' '}
							emitida pelo seu CNPJ em favor da Plumaa (ARAUJO
							&amp; SILVA LTDA · CNPJ 65.321.886/0001-10), com
							descrição equivalente a &quot;prestação de serviços
							de teleatendimento em saúde — (sua
							especialidade)&quot;.
						</p>
						<p className="font-albert mt-1.5 text-[14px] leading-[1.7] text-zinc-800">
							Sem nota emitida, o repasse fica retido até a
							regularização. Não há multa — apenas a espera.
						</p>
					</div>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Regras que afetam o valor pago
					</h2>
					<p className="font-albert mb-1 text-[14.5px] leading-[1.75] text-zinc-600">
						Algumas situações específicas alteram o que entra (ou
						não) no seu repasse. Vale conhecer:
					</p>
					<ul className="list-none">
						{valueRules.map((content, i) => (
							<li
								key={i}
								className="font-albert flex gap-3 border-b border-zinc-100 py-3 text-[14px] leading-[1.7] text-zinc-600 last:border-b-0"
							>
								<span
									className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400"
									aria-hidden
								/>
								<span>{content}</span>
							</li>
						))}
					</ul>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Quando esta tabela pode mudar
					</h2>
					<div className="font-albert space-y-3.5 text-[14.5px] leading-[1.75] text-zinc-600">
						<p>
							Esta tabela é a versão oficial vigente. A Plumaa
							pode atualizá-la a qualquer momento — em geral pra
							ajustar a faixas horárias, especialidades ou
							contexto de mercado.
						</p>
						<p>Quando há alteração:</p>
					</div>

					<ul className="mt-1 list-none">
						{tableChangeRules.map((content, i) => (
							<li
								key={i}
								className="font-albert flex gap-3 border-b border-zinc-100 py-3 text-[14px] leading-[1.7] text-zinc-600 last:border-b-0"
							>
								<span
									className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400"
									aria-hidden
								/>
								<span>{content}</span>
							</li>
						))}
					</ul>

					<p className="font-albert mt-4 text-[14.5px] leading-[1.75] text-zinc-600">
						Por isso, vale o hábito de consultar esta página de
						tempos em tempos. Se algo mudou, você verá aqui — sem
						precisar abrir o contrato.
					</p>
				</section>

				<section className="mx-auto mt-10 max-w-[820px]">
					<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.15] text-zinc-900">
						Dúvidas sobre repasse
					</h2>
					<div className="font-albert space-y-3.5 text-[14.5px] leading-[1.75] text-zinc-600">
						<p>
							Se faltou um Pix, a NF foi emitida e o repasse não
							chegou, ou o valor de uma sessão saiu diferente da
							tabela:
						</p>
						<p>
							<strong className="font-semibold text-zinc-900">
								E-mail:
							</strong>{' '}
							<a
								href="mailto:contato@plumaa.com.br"
								className="text-brand-orange hover:underline"
							>
								contato@plumaa.com.br
							</a>
							<br />
							<strong className="font-semibold text-zinc-900">
								WhatsApp:
							</strong>{' '}
							<a
								href={WHATSAPP_LINK_FORM}
								target="_blank"
								rel="noopener noreferrer"
								className="text-brand-orange hover:underline"
							>
								+55 {WHATSAPP_NUMBER_FORMATTED}
							</a>
						</p>
						<p>
							Esta página é uma referência prática. As condições
							contratuais detalhadas — incluindo direitos, deveres
							e regime fiscal — estão nos{' '}
							<Link
								to="/termos-de-uso-do-profissional"
								className="text-brand-orange underline underline-offset-4"
							>
								Termos de Credenciamento do Profissional
							</Link>
							, aceitos no momento do credenciamento.
						</p>
					</div>
				</section>
			</main>

			<Footer />
		</section>
	);
};

export { ProfessionalRepassePage };
